import React, { useEffect, useState } from 'react';
import { useSite } from '../../context/SiteContext';
import { adminCreateService, adminUpdateService, adminDeleteService } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { Plus, Trash2, Save, X, Pencil, Loader2, Sparkles } from 'lucide-react';
import { I18nInput, bulkTranslate } from './I18nInput';
import { pickI18n } from '../../i18n/helpers';

const empty = {
  order: 0,
  code: '',
  icon: 'Film',
  title: { es: '', en: '' },
  short: { es: '', en: '' },
  description: { es: '', en: '' },
  features: [],
  image: '',
};

const Field = ({ label, children, full }) => (
  <label className={`block ${full ? 'sm:col-span-2' : ''}`}>
    <span className="block text-[11px] uppercase tracking-widest text-white/50 mb-2">{label}</span>
    {children}
  </label>
);

const AdminServices = () => {
  const { services, reload } = useSite();
  const { toast } = useToast();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    if (editing === null) setForm(empty);
    else if (editing === 'new') setForm({ ...empty, order: (services?.length || 0) + 1 });
    else {
      const s = services.find((x) => x.id === editing);
      if (s) setForm(s);
    }
  }, [editing, services]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, features: form.features || [] };
      if (editing === 'new') {
        await adminCreateService(payload);
        toast({ title: 'Servicio creado' });
      } else {
        await adminUpdateService(editing, payload);
        toast({ title: 'Servicio actualizado' });
      }
      await reload();
      setEditing(null);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo guardar' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar este servicio?')) return;
    await adminDeleteService(id);
    await reload();
    toast({ title: 'Servicio eliminado' });
  };

  const translateAll = async () => {
    setTranslating(true);
    try {
      const items = [
        { value: form.title, set: (v) => setForm((f) => ({ ...f, title: v })) },
        { value: form.short, set: (v) => setForm((f) => ({ ...f, short: v })) },
        { value: form.description, set: (v) => setForm((f) => ({ ...f, description: v })) },
      ];
      (form.features || []).forEach((feat, i) => {
        items.push({ value: feat, set: (v) => {
          setForm((f) => {
            const arr = [...(f.features || [])]; arr[i] = v;
            return { ...f, features: arr };
          });
        }});
      });
      await bulkTranslate(items, toast);
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#00e0c7] mb-3">Catálogo</div>
          <h1 className="font-display text-4xl text-white font-light">Servicios</h1>
        </div>
        {!editing && (
          <button onClick={() => setEditing('new')} className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-[#00e0c7] text-[#0d1b2a] font-semibold text-sm">
            <Plus className="w-4 h-4" /> Nuevo servicio
          </button>
        )}
      </div>

      {editing ? (
        <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <h2 className="text-white text-lg">{editing === 'new' ? 'Nuevo servicio' : 'Editar servicio'}</h2>
            <div className="flex items-center gap-2">
              <button onClick={translateAll} disabled={translating} className="inline-flex items-center gap-2 px-4 h-9 rounded-full border border-[#00e0c7]/30 bg-[#00e0c7]/10 text-[#9be8dc] hover:bg-[#00e0c7]/20 text-xs font-medium transition-colors disabled:opacity-50">
                {translating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Traducir a EN
              </button>
              <button onClick={() => setEditing(null)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Orden"><input type="number" className="ldc-input" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} /></Field>
            <Field label="Código (S/01)"><input className="ldc-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></Field>
            <Field label="Icono (Film, Cloud, KeyRound, Radio, Printer)"><input className="ldc-input" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} /></Field>
            <Field label="Imagen URL"><input className="ldc-input" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} /></Field>
            <div className="sm:col-span-2"><I18nInput label="Título" value={form.title} onChange={(v) => setForm({ ...form, title: v })} /></div>
            <div className="sm:col-span-2"><I18nInput label="Descripción corta" value={form.short} onChange={(v) => setForm({ ...form, short: v })} /></div>
            <div className="sm:col-span-2"><I18nInput label="Descripción completa" value={form.description} onChange={(v) => setForm({ ...form, description: v })} multiline rows={5} /></div>
            <div className="sm:col-span-2">
              <div className="text-[11px] uppercase tracking-widest text-white/50 mb-2">Features</div>
              <div className="space-y-2">
                {(form.features || []).map((feat, i) => (
                  <div key={i} className="flex gap-2 items-end">
                    <div className="flex-1"><I18nInput value={feat} onChange={(v) => {
                      const arr = [...(form.features || [])]; arr[i] = v;
                      setForm({ ...form, features: arr });
                    }} /></div>
                    <button onClick={() => setForm({ ...form, features: (form.features || []).filter((_, j) => j !== i) })} className="p-2 text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <button onClick={() => setForm({ ...form, features: [...(form.features || []), { es: '', en: '' }] })} className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg border border-dashed border-white/15 text-white/60 hover:text-white text-[11px] transition-colors">
                  <Plus className="w-3 h-3" /> Añadir feature
                </button>
              </div>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setEditing(null)} className="px-5 h-11 rounded-full border border-white/10 text-white/70 text-sm hover:bg-white/5">Cancelar</button>
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-[#00e0c7] text-[#0d1b2a] font-semibold text-sm disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {(services || []).map((s) => (
            <div key={s.id} className="p-5 rounded-2xl border border-white/8 bg-white/[0.02] flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#00e0c7]/10 border border-[#00e0c7]/20 flex items-center justify-center text-[#00e0c7] font-mono text-xs flex-shrink-0">{s.code}</div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-medium truncate">{pickI18n(s.title, 'es')}</div>
                <div className="text-white/50 text-sm truncate">{pickI18n(s.short, 'es')}</div>
              </div>
              <div className="text-white/30 text-xs hidden md:block">Orden #{s.order}</div>
              <button onClick={() => setEditing(s.id)} className="p-2 text-white/50 hover:text-[#00e0c7] transition-colors"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(s.id)} className="p-2 text-white/50 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminServices;
